import Q from 'q';
import * as utils from '../../shared/utils';
import Logger from '../../services/Logger';
import { showErrorDialog } from '../../components/dialog';
import l18n from '../../shared/strings';

/**
 * Download file by url
 * @param {String} url
 * @param {String?} responseType
 * @returns {Object} { promise: Promise, cancel: Function }
 *   @param {Promise} promise - Promise that returns file (blob by default)
 *   @param {Function} cancel - function to cancel download
 * }
 */
function downloadFile(url, responseType = 'blob') {
  const xhr = new XMLHttpRequest();
  let cancelled = false;

  function cancel() {
    cancelled = true;
    xhr.abort();
  }

  const promise = (() => Q.Promise((resolve, reject, notify) => {
    let numberOfAttempts = 1;

    const buildError = (err) => {
      if (err instanceof Error) {
        return err;
      }

      const xhrTarget = err && (err.currentTarget || err.target);
      if (xhrTarget && typeof xhrTarget.status === 'number') {
        const { status, statusText, responseURL } = xhrTarget;
        const message = `Can not download file from GD: ${status}${statusText ? ` ${statusText}` : ''}`;
        const error = new Error(message);
        error.status = status;
        error.statusText = statusText;
        error.url = responseURL || url;
        return error;
      }

      if (typeof err === 'string') {
        return new Error(err);
      }

      return new Error('Can not download file from GD');
    };

    const handleError = (err) => {
      const error = buildError(err);
      const connection = utils.getNavigatorConnectionInfo();
      const errorMessageForUser = l18n.DIALOGS.DOWNLOAD_ERROR.TEXT;
      Logger.error(error, { error: err, url }, [
        'DownloadFileFromGDFailed',
        { errorMessage: error.message || 'NoMessage', userDialogueMessage: errorMessageForUser, connection },
      ]);
      showErrorDialog(errorMessageForUser, l18n.DIALOGS.DOWNLOAD_ERROR.TITLE);
      reject(error);
    };

    const handleProgress = (event) => {
      if (event.lengthComputable) {
        notify({ loaded: event.loaded, total: event.total, xhr });
      }
    };

    const handleLoad = (resp) => {
      const { status, statusText, responseURL } = resp.currentTarget;

      if (status === 200) {
        const file = resp.currentTarget.response;
        resolve(file);
      } else {
        const error = new Error(
          `Can not download file from GD: ${status}${statusText ? ` ${statusText}` : ''}`,
        );
        error.status = status;
        error.statusText = statusText;
        error.url = responseURL || url;

        handleError(error);
      }
    };

    const doRequest = () => {
      xhr.open('GET', url, true);
      xhr.responseType = responseType;

      xhr.onload = handleLoad;
      xhr.onerror = handleError;
      xhr.onprogress = handleProgress;
      xhr.send();
    };

    const handleAbort = () => {
      if (cancelled) return;

      if (numberOfAttempts > 3) {
        handleError(new Error(`Can not download file from GD, request aborted ${numberOfAttempts} times`));
        return;
      }
      numberOfAttempts += 1;
      /** exponential backoff */
      setTimeout(doRequest, numberOfAttempts * 500);
    };

    xhr.onabort = handleAbort;

    doRequest();
  }))();

  return { promise, cancel };
}

export default downloadFile;
